import { useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Check, ArrowRight } from "lucide-react";
import { api, formatErr } from "../lib/api";

export default function PlaybookDownload() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", company: "" });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone || !form.email) {
            toast.error("Please fill in name, email and phone.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/leads", {
                name: form.name,
                email: form.email,
                phone: form.phone,
                city: form.city || "Not specified",
                message: form.company ? `Company: ${form.company}` : "",
                source: "playbook_download",
            });
            setDone(true);
            toast.success("Playbook unlocked. Enjoy the read.");
            // Auto-download
            const link = document.createElement("a");
            link.href = "/lead-krafts-playbook.pdf";
            link.download = "Lead-Krafts-12-Month-Growth-Playbook.pdf";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail) || "Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="playbook"
            data-testid="playbook-section"
            className="py-24 sm:py-32 px-6 sm:px-10 lg:px-16 border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    {/* Left: mockup */}
                    <div className="lg:col-span-5">
                        <div className="relative">
                            {/* Fake PDF cover */}
                            <div className="relative w-full max-w-[360px] mx-auto aspect-[3/4] bg-gradient-to-br from-[#12121A] to-black border border-[#D4AF37]/40 shadow-[0_30px_80px_rgba(212,175,55,0.15)] p-8 flex flex-col rotate-[-3deg]">
                                <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-[#D4AF37]"
                                    style={{ clipPath: "polygon(100% 0, 100% 100%, 0 0)" }} />
                                <div className="relative flex items-center gap-2 mb-8">
                                    <span className="h-6 w-6 border border-[#D4AF37] text-[#D4AF37] font-serif-display text-xs grid place-items-center">L</span>
                                    <span className="font-serif-display text-sm text-white/80">Lead Krafts</span>
                                </div>
                                <div className="relative mt-auto">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">The Playbook</div>
                                    <div className="font-serif-display text-3xl text-white leading-tight">The 12-Month</div>
                                    <div className="font-serif-display text-3xl italic text-[#D4AF37] leading-tight">Growth Playbook</div>
                                    <div className="mt-4 text-[10px] text-white/50 leading-relaxed">
                                        India's real estate lead generation blueprint · 2026 edition
                                    </div>
                                </div>
                            </div>
                            {/* Behind card */}
                            <div className="absolute inset-0 max-w-[360px] mx-auto aspect-[3/4] bg-[#0A0B10] border border-white/10 -z-10 rotate-[3deg] translate-x-4 translate-y-4" />
                        </div>
                    </div>

                    {/* Right: form */}
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="h-px w-10 bg-[#D4AF37]" />
                            <span className="overline">Free Playbook · PDF</span>
                        </div>
                        <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                            The exact playbook we use <br />
                            to <span className="italic text-[#D4AF37]">10x developer sales</span>.
                        </h2>
                        <p className="mt-6 text-white/70 leading-relaxed max-w-xl">
                            Download the full 12-month blueprint — Phase-wise strategy, KPI targets, channel mix,
                            sales-conversion tactics. Everything we execute for our developer partners.
                        </p>

                        {!done ? (
                            <form
                                onSubmit={submit}
                                data-testid="playbook-form"
                                className="mt-8 bg-[#0A0B10] border border-[#D4AF37]/25 p-6 sm:p-8"
                            >
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <input
                                        data-testid="pb-name"
                                        className="hol-input"
                                        placeholder="Full name*"
                                        value={form.name}
                                        onChange={(e) => set("name", e.target.value)}
                                    />
                                    <input
                                        data-testid="pb-company"
                                        className="hol-input"
                                        placeholder="Company / Project"
                                        value={form.company}
                                        onChange={(e) => set("company", e.target.value)}
                                    />
                                    <input
                                        data-testid="pb-phone"
                                        className="hol-input"
                                        placeholder="Phone (WhatsApp)*"
                                        value={form.phone}
                                        onChange={(e) => set("phone", e.target.value)}
                                    />
                                    <input
                                        data-testid="pb-email"
                                        type="email"
                                        className="hol-input"
                                        placeholder="Work email*"
                                        value={form.email}
                                        onChange={(e) => set("email", e.target.value)}
                                    />
                                    <input
                                        data-testid="pb-city"
                                        className="hol-input sm:col-span-2"
                                        placeholder="Primary city / micro-market"
                                        value={form.city}
                                        onChange={(e) => set("city", e.target.value)}
                                    />
                                </div>
                                <button
                                    data-testid="pb-submit"
                                    type="submit"
                                    disabled={loading}
                                    className="btn-gold mt-6 w-full justify-center disabled:opacity-60"
                                >
                                    {loading ? "Preparing your PDF..." : (
                                        <>
                                            <Download size={16} /> Download the free playbook
                                        </>
                                    )}
                                </button>
                                <p className="mt-4 text-[11px] text-white/40 flex items-center gap-2 justify-center">
                                    <FileText size={12} /> PDF · 6 pages · No spam. Ever.
                                </p>
                            </form>
                        ) : (
                            <div
                                data-testid="playbook-success"
                                className="mt-8 bg-[#0A0B10] border border-[#D4AF37]/40 p-8"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="shrink-0 h-11 w-11 border border-[#D4AF37] grid place-items-center text-[#D4AF37]">
                                        <Check size={20} strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <h3 className="font-serif-display text-2xl">Playbook unlocked.</h3>
                                        <p className="mt-2 text-sm text-white/60">
                                            Your download should have started automatically. If not,{" "}
                                            <a href="/lead-krafts-playbook.pdf" download className="text-[#D4AF37] underline">
                                                click here
                                            </a>
                                            . We'll also share advanced templates on WhatsApp shortly.
                                        </p>
                                        <a href="#lead" className="btn-outline-gold mt-6 text-sm">
                                            Book a strategy call <ArrowRight size={14} />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
